import {
  Box,
  type BoxProps,
  HStack,
  type StackProps,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  type TextProps,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import React from "react";
import EntityModalButton from "./EntityModalButton";

type SortDirection = "asc" | "desc";
type SortValue = number | string | null | undefined;
type TableAlignment = "center" | "left" | "right";
type TableCellStyleProps = React.ComponentProps<typeof Td>;
type DataTableStyleProps = React.ComponentProps<typeof Table>;
type TableHeaderStyleProps = React.ComponentProps<typeof Th>;
type TableRowStyleProps = React.ComponentProps<typeof Tr>;

export type EntityProfileDataTableColumn<T, SortKey extends string> = {
  cellAlign?: TableAlignment;
  cellProps?: TableCellStyleProps | ((row: T) => TableCellStyleProps);
  headerAlign?: TableAlignment;
  header: React.ReactNode;
  headerProps?: React.ComponentProps<typeof Th>;
  key: string;
  render: (row: T, index: number) => React.ReactNode;
  sortKey?: SortKey;
  sortValue?: (row: T) => SortValue;
};

type Props<T, SortKey extends string> = {
  columns: Array<EntityProfileDataTableColumn<T, SortKey>>;
  defaultCellAlign?: TableAlignment;
  defaultHeaderAlign?: TableAlignment;
  defaultPageSize?: number;
  defaultSort?: {
    direction: SortDirection;
    key: SortKey;
  };
  emptyLabel?: React.ReactNode;
  getRowKey: (row: T, index: number) => React.Key;
  minW?: DataTableStyleProps["minW"];
  pageSizeOptions?: number[];
  renderExpandedRow?: (row: T, index: number) => React.ReactNode;
  rowProps?: (row: T, index: number) => TableRowStyleProps;
  rows: T[];
  showEmptyState?: boolean;
  tableSx?: DataTableStyleProps["sx"];
};

const defaultPageSizeOptions = [15, 25, 50, 100];

const tableViewportProps: BoxProps = {
  maxW: "100%",
  overflowX: "auto",
};

const tableContainerProps: BoxProps = {
  overflowX: "auto",
};

const headerCellBaseProps: TableHeaderStyleProps = {
  borderColor: "#ddd",
  color: "#111827",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: "1.1",
  px: 2,
  py: 2.5,
  textTransform: "none",
  verticalAlign: "middle",
  whiteSpace: "normal",
};

const headerContentProps: StackProps = {
  as: "span",
  display: "flex",
  spacing: 1,
  verticalAlign: "middle",
  w: "100%",
};

const headerLabelProps: TextProps = {
  as: "span",
  display: "block",
};

const sortIndicatorProps: BoxProps = {
  color: "#b8b8b8",
  lineHeight: "0.7",
};

const sortArrowBaseProps: TextProps = {
  fontSize: "9px",
};

const emptyCellProps: TableCellStyleProps = {
  borderColor: "#ddd",
  color: "#6b7280",
  textAlign: "center",
};

const dataCellBaseProps: TableCellStyleProps = {
  borderColor: "#ddd",
};

const paginationShellProps: StackProps = {
  justifyContent: "space-between",
  mt: 2,
  spacing: 3,
};

const paginationGroupProps: StackProps = {
  spacing: 0,
};

const paginationButtonStyle: React.CSSProperties = {
  borderRadius: 0,
  fontSize: "12px",
  height: 28,
  minWidth: 30,
};

const pageSizeButtonStyle: React.CSSProperties = {
  ...paginationButtonStyle,
  minWidth: 34,
};

/** Bootstrap has no "pressed" button state, so the active page is tinted inline. */
const activePaginationBg = "#e9e9e9";

function getJustifyContent(align: TableAlignment) {
  switch (align) {
    case "center":
      return "center";
    case "right":
      return "flex-end";
    case "left":
      return "flex-start";
  }
}

function compareSortValues(firstValue: SortValue, secondValue: SortValue) {
  if (typeof firstValue === "number" && typeof secondValue === "number") {
    return firstValue - secondValue;
  }

  if ((firstValue === null || firstValue === undefined || firstValue === "") && secondValue) {
    return 1;
  }

  if (firstValue && (secondValue === null || secondValue === undefined || secondValue === "")) {
    return -1;
  }

  return String(firstValue ?? "").localeCompare(String(secondValue ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export default function EntityProfileDataTable<T, SortKey extends string>(
  props: Props<T, SortKey>
) {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(
    props.defaultPageSize ?? props.pageSizeOptions?.[0] ?? defaultPageSizeOptions[0]
  );
  const [sort, setSort] = React.useState(props.defaultSort ?? null);
  const isPaginated = props.pageSizeOptions !== undefined || props.defaultPageSize !== undefined;
  const pageSizeOptions = props.pageSizeOptions ?? defaultPageSizeOptions;
  const sortableColumnsByKey = React.useMemo(() => {
    const entries = props.columns.flatMap((column) =>
      column.sortKey === undefined ? [] : [[column.sortKey, column] as const]
    );

    return new Map(entries);
  }, [props.columns]);
  const sortedRows = React.useMemo(() => {
    if (sort === null) {
      return props.rows;
    }

    const sortColumn = sortableColumnsByKey.get(sort.key);

    const getSortValue =
      sortColumn?.sortValue ?? ((row: T) => String(sortColumn?.render(row, 0) ?? ""));

    return [...props.rows].sort((firstRow, secondRow) => {
      const comparison = compareSortValues(getSortValue(firstRow), getSortValue(secondRow));

      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [props.rows, sort, sortableColumnsByKey]);
  const pageCount = isPaginated ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const visibleRows = React.useMemo(
    () =>
      isPaginated ? sortedRows.slice(page * pageSize, page * pageSize + pageSize) : sortedRows,
    [isPaginated, page, pageSize, sortedRows]
  );

  React.useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, Math.max(pageCount - 1, 0)));
  }, [pageCount]);

  const handleSort = (sortKey: SortKey) => {
    setPage(0);
    setSort((currentSort) => {
      if (currentSort?.key !== sortKey) {
        return { key: sortKey, direction: "asc" };
      }

      if (currentSort.direction === "asc") {
        return { key: sortKey, direction: "desc" };
      }

      return null;
    });
  };

  return (
    <Box {...tableViewportProps}>
      <TableContainer {...tableContainerProps}>
        <Table minW={props.minW} size="sm" sx={props.tableSx} variant="simple">
          <Thead>
            <Tr>
              {props.columns.map((column) => {
                const isSortable = column.sortKey !== undefined;
                const isActive = isSortable && sort?.key === column.sortKey;
                const headerAlign = column.headerAlign ?? props.defaultHeaderAlign ?? "left";

                return (
                  <Th
                    key={column.key}
                    bg={isActive ? "#d9edf7" : undefined}
                    cursor={isSortable ? "pointer" : "default"}
                    textAlign={headerAlign}
                    {...headerCellBaseProps}
                    {...column.headerProps}
                    onClick={() => {
                      if (column.sortKey !== undefined && isSortable) {
                        handleSort(column.sortKey);
                      }
                    }}
                  >
                    <HStack justifyContent={getJustifyContent(headerAlign)} {...headerContentProps}>
                      <Text textAlign={headerAlign} {...headerLabelProps}>
                        {column.header}
                      </Text>
                      {isSortable ? (
                        <Box {...sortIndicatorProps}>
                          <Text
                            color={isActive && sort?.direction === "asc" ? "#777" : undefined}
                            fontWeight={isActive && sort?.direction === "asc" ? 700 : 400}
                            {...sortArrowBaseProps}
                          >
                            ▲
                          </Text>
                          <Text
                            color={isActive && sort?.direction === "desc" ? "#777" : undefined}
                            fontWeight={isActive && sort?.direction === "desc" ? 700 : 400}
                            {...sortArrowBaseProps}
                          >
                            ▼
                          </Text>
                        </Box>
                      ) : null}
                    </HStack>
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {sortedRows.length === 0 && props.showEmptyState !== false ? (
              <Tr>
                <Td colSpan={props.columns.length} {...emptyCellProps}>
                  {props.emptyLabel ?? "No data"}
                </Td>
              </Tr>
            ) : sortedRows.length > 0 ? (
              visibleRows.map((row, index) => {
                const absoluteIndex = page * pageSize + index;

                return (
                  <React.Fragment key={props.getRowKey(row, absoluteIndex)}>
                    <Tr
                      bg={absoluteIndex % 2 === 0 ? "#f9f9f9" : "#fff"}
                      {...props.rowProps?.(row, absoluteIndex)}
                    >
                      {props.columns.map((column) => {
                        const cellProps =
                          typeof column.cellProps === "function"
                            ? column.cellProps(row)
                            : column.cellProps;
                        const cellAlign = column.cellAlign ?? props.defaultCellAlign ?? "left";

                        return (
                          <Td
                            key={column.key}
                            textAlign={cellAlign}
                            {...dataCellBaseProps}
                            {...cellProps}
                          >
                            {column.render(row, absoluteIndex)}
                          </Td>
                        );
                      })}
                    </Tr>
                    {props.renderExpandedRow?.(row, absoluteIndex)}
                  </React.Fragment>
                );
              })
            ) : null}
          </Tbody>
        </Table>
      </TableContainer>
      {sortedRows.length > 0 && isPaginated && props.pageSizeOptions !== undefined ? (
        <HStack {...paginationShellProps}>
          {sortedRows.length > pageSize ? (
            <HStack {...paginationGroupProps}>
              <EntityModalButton
                aria-label="Previous page"
                disabled={page === 0}
                iconClassName="fa fa-chevron-left"
                kind="white"
                size="xs"
                style={paginationButtonStyle}
                onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 0))}
              />
              {Array.from({ length: pageCount }).map((_, pageIndex) => (
                <EntityModalButton
                  key={pageIndex}
                  kind="white"
                  size="xs"
                  style={{
                    ...paginationButtonStyle,
                    background: pageIndex === page ? activePaginationBg : undefined,
                  }}
                  onClick={() => setPage(pageIndex)}
                >
                  {pageIndex + 1}
                </EntityModalButton>
              ))}
              <EntityModalButton
                aria-label="Next page"
                disabled={page >= pageCount - 1}
                iconClassName="fa fa-chevron-right"
                kind="white"
                size="xs"
                style={paginationButtonStyle}
                onClick={() => setPage((currentPage) => Math.min(currentPage + 1, pageCount - 1))}
              />
            </HStack>
          ) : (
            <Box />
          )}
          <HStack {...paginationGroupProps}>
            {pageSizeOptions.map((option) => (
              <EntityModalButton
                key={option}
                kind="white"
                size="xs"
                style={{
                  ...pageSizeButtonStyle,
                  background: option === pageSize ? activePaginationBg : undefined,
                }}
                onClick={() => {
                  setPageSize(option);
                  setPage(0);
                }}
              >
                {option}
              </EntityModalButton>
            ))}
          </HStack>
        </HStack>
      ) : null}
    </Box>
  );
}
